<template>
  <div>
    <Head title="Projection Entries" />
    <h1 class="mb-8 text-3xl font-bold"><Link class="text-orange-600 hover:text-orange-500" href="/projections">Projection: {{ projection.financial_year }}</Link></h1>
    <div class="flex items-center justify-between mb-6">
      <a class="btn-orange" href="#" @click="openAdjustmentWindow">
        <span>Adjust</span>
      </a>
      <span class="text-gray-500 text-sm">Last updated: {{ projection.created_at }} - {{ projection.updated_by }}</span>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Main Category</th>
          <th class="pb-3 pt-3 px-3">Sub Category</th>
          <th class="pb-3 pt-3 px-3">UoM</th>
          <th v-for="(index, field) in field_titles.others" :key="index" class="pb-3 pt-3 px-3">{{ field }}</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="item in dataItems" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <a class="flex items-center px-3 py-3 focus:text-orange-500" href="#">
              {{ item.main_category }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center px-3 py-3 focus:text-orange-500" href="#">
              {{ item.sub_category }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center px-3 py-3 focus:text-orange-500" href="#">
              {{ item.uom }}
            </a>
          </td>
          <td v-for="(field, indx) in item.fields" :key="indx" class="border-t">
            <a class="flex items-center px-3 py-3 focus:text-orange-500" href="#">
              {{ field[indx+1] }}
            </a>
          </td>
          <td class="w-px border-t">
            <a
              v-if="user.group_permissions.includes('add_projections')
                || user.per_user_permissions.includes('add_projections')"
              class="flex items-center px-3" href="#" tabindex="-1" @click="deleteEntry(item)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </a>
          </td>
        </tr>
        <tr v-if="dataItems.length === 0">
          <td class="px-3 py-3 border-t" colspan="5">No data found</td>
        </tr>
      </table>
    </div>
  </div>
  <div v-if="openAdjustment" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Adjustment Paramenters</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form @submit.prevent="updateProjections">
                <div class="flex flex-wrap -mb-2 -mr-6 pt-2 pl-8 pr-3 pb-2">
                  <select-input v-model="formData.operation" :error="$props.errors.operation" class="pb-2 pr-6 w-full lg:w-full" label="Operation">
                    <option :value="null">-Select Operation-</option>
                    <option v-for="item in operations" :key="item" :value="item">{{ item }}</option>
                  </select-input>
                </div>
                <div class="flex flex-wrap -mb-0 -mr-6 pt-2 pl-8 pr-3 pb-2">
                  <text-input v-model="formData.variable" class="pb-2 pr-6 w-full lg:w-full" placeholder="Variable unit" label="Amount" />
                  <div v-if="$props.errors" class="form-error">{{ $props.errors.variable }}</div>
                </div>
                <div class="flex flex-wrap -mb-2 -mr-6 pt-0 pl-8 pr-8 pb-2">
                  <p class="pt-1 pb-1 mb-1">Categories:</p>
                  <multiselect
                    id="category"
                    v-model="formData.categories"
                    placeholder="Search Categories"
                    :options="listCategories"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="true"
                    mode="tags"
                    class="mt-1"
                    :close-on-select="false"
                    :create-option="true"
                    :error="$props.errors.categories"
                    :style="$props.errors.categories ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                  />
                  <div v-if="$props.errors" class="form-error">{{ $props.errors.categories }}</div>
                </div>
                <div class="flex flex-wrap -mb-2 -mr-6 pt-0 pl-8 pr-8 pb-8">
                  <p class="pt-1 pb-1 mb-1">Months:</p>
                  <multiselect
                    id="month"
                    v-model="formData.months"
                    placeholder="Search Month"
                    :options="listMonths"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="true"
                    mode="tags"
                    class="mt-1"
                    :close-on-select="false"
                    :create-option="true"
                    :error="$props.errors.months"
                    :style="$props.errors.months ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                  />
                  <div v-if="$props.errors" class="form-error">{{ $props.errors.months }}</div>
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="updateProjections">
                Update
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="openEdit" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Update {{ selectedItem.main_category }}/{{ selectedItem.sub_category }}</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form enctype="multipart/form-data" @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-8 pr-8 pb-8">
                  <text-input v-for="(index, field) in field_titles.others" :key="index" v-model="formData.units" :value="index" class="pb-8 pr-6 w-1/3 lg:w-1/3" :label="field" />
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="update">
                Update
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="openDelete" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">Delete {{ selectedItem.category }}</h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-4 pl-2 pr-2 pb-8">
                  <p>You are about to delete {{ selectedItem.category }} from {{ projection.financial_year }} projections, do you wish to continue?</p>
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="deleteItem">
                Yes
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
      
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import Multiselect from '@vueform/multiselect'
    
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    TextInput,
    SelectInput,
    Multiselect,
  },
  layout: Layout,
  props: {
    dataItems: Array,
    field_titles: Array,
    projection: Object,
    errors: Object,
    listCategories: Object,
    listMonths: Object,
  },
  data() {
    return {
      formData: {
        operation: null,
        categories: null,
        months: null,
        variable: null,
      },
      operations: [
        'Increase by %',
        'Decrease by %',
        'Increase by an absolute number',
        'Decrease by an absolute number',
      ],
      openEdit: false,
      openDelete: false,
      selectedItem: null,
      checkSuccess: false,
      openAdjustment: false,
    }
  },
  computed: {
    user() {
      return this.$inertia.page.props.auth.user
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openEditWindow() {
      this.openEdit = true
    },
    closeModal() {
      this.openEdit = false
      this.openDelete = false
      this.openAdjustment = false
    },
    openAdjustmentWindow() {
      this.openAdjustment = true
    },
    editEntry(item) {
      if (this.user.group_permissions.includes('add_projections')
          || this.user.per_user_permissions.includes('add_projections'))
      {
        this.selectedItem = item
        this.formData.units = item.units
        this.openEdit = true
      }
    },
    deleteEntry(item) {
      this.selectedItem = item
      this.openDelete = true
    },
    updateProjections() {
      this.formData.id = this.projection.id
      this.$inertia.post(`/projection/${this.projection.id}/adjust-projection`, this.formData, {
        onSuccess: () => {
          this.openAdjustment = false
        },
      })
    },
    update() {
      this.formData._method = 'put'
      this.formData.id = this.projection.id
      this.$inertia.post(`/projection/${this.projection.id}/projection-entries/${this.selectedItem.id}`, this.formData, {
        onSuccess: () => {
          this.openEdit = false
        },
      })
    },
    deleteItem() {
      this.formData._method = 'post'
      this.formData.id = this.projection.id
      this.$inertia.delete(`/projection/${this.projection.id}/projection-entries/${this.selectedItem.id}`, this.formData, {
        onSuccess: () => {
          this.checkSuccess = true
        },
        onError: () => {
          this.checkSuccess = false
        },
      })
      this.openDelete = false
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
      